import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, interval, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '@app/login/_services/authentication.service';
import { TechnicalInspectionService } from '@app/technical-inspections/_services/technical-inspection.service';
import { TechnicalPointService } from '@app/tech-points/_services/technical-point.service';
import { TechnicalInspection } from '@app/technical-inspections/_models/technical-inspection.model';
import { TechInspectionService } from '@models/tech-inspection-service.model';

const STARTED_STATUS_CODE = 'STARTED';
// Service type code of a main ("Основна услуга") service — ServiceTypeEnum.BASIC_SERVICE.
const MAIN_SERVICE_TYPE_CODE = 'M';
const REFRESH_INTERVAL_MS = 30000;
const CLOCK_INTERVAL_MS = 1000;
const NEXT_LIMIT = 5;

interface BoardCard {
  lineNumber: number | null;
  inspection: TechnicalInspection | null;
}

@Component({
  selector: 'app-reservation-screen',
  templateUrl: './reservation-screen.component.html',
  styleUrls: ['./reservation-screen.component.css']
})
export class ReservationScreenComponent implements OnInit, OnDestroy {

  technicalPointId: number | null = null;
  technicalPointName = '';
  lineNumbers: number[] = [];

  inProgressCards: BoardCard[] = [];
  nextInspections: TechnicalInspection[] = [];

  currentDate = '';
  currentTime = '';

  loadFailed = false;

  private refreshSub?: Subscription;
  private clockSub?: Subscription;
  private setupRetrySub?: Subscription;

  constructor(
    private techInspService: TechnicalInspectionService,
    private techPointService: TechnicalPointService,
    private auth: AuthenticationService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.updateClock();
    this.clockSub = interval(CLOCK_INTERVAL_MS).subscribe(() => this.updateClock());

    const boardKey = this.route.snapshot.queryParamMap.get('key');
    if (boardKey) {
      // Strip the key from the address bar immediately (before the exchange
      // completes) so it is never visible while the board is loading.
      this.stripUrlKey();
      // Exchange the captured key for a JWT, then load the board.
      this.auth.boardLogin(boardKey).subscribe({
        next: () => this.resolveTechnicalPoint(),
        error: () => this.loadFailed = true
      });
    } else {
      // Normal navigation / already-logged-in user.
      this.resolveTechnicalPoint();
    }
  }

  // Strip the ?key= from the address bar so it doesn't linger in the URL or
  // browser history.
  private stripUrlKey(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
    this.clockSub?.unsubscribe();
    this.setupRetrySub?.unsubscribe();
  }

  public logout(): void {
    this.auth.logout();
  }

  private resolveTechnicalPoint(): void {
    const mobileAppIds = AuthenticationService.getEmployeeTechPointsIds();
    if (mobileAppIds.length === 0) {
      return;
    }

    this.techPointService.findTechnicalPointsByIds(mobileAppIds).subscribe({
      next: points => {
        if (!points || points.length === 0) {
          this.scheduleSetupRetry();
          return;
        }

        const point = points[0];
        this.technicalPointId = point.id ?? null;
        this.technicalPointName = point.name;

        if (this.technicalPointId == null) {
          this.scheduleSetupRetry();
          return;
        }

        // Point resolved — clear any error screen left by an earlier attempt.
        this.loadFailed = false;
        this.setupRetrySub?.unsubscribe();
        this.setupRetrySub = undefined;

        // The by-ids response does not include lines, so load the valid lines
        // separately (same as the КТП inspections list does). A failure here is
        // not fatal — buildCards falls back to one card per started inspection
        // when line metadata is missing, so start polling either way.
        this.techPointService.findValidLinesByPointId(this.technicalPointId).subscribe({
          next: lines => {
            this.lineNumbers = (lines ?? [])
              .map(line => line.number)
              .sort((a, b) => a - b);
            this.startPolling();
          },
          error: () => {
            this.lineNumbers = [];
            this.startPolling();
          }
        });
      },
      error: () => this.scheduleSetupRetry()
    });
  }

  // A board is an unattended screen with nobody around to press F5, so a
  // failure to resolve the technical point retries on the refresh interval
  // instead of parking on the error message forever.
  private scheduleSetupRetry(): void {
    this.loadFailed = true;

    if (this.setupRetrySub) {
      return;
    }

    this.setupRetrySub = interval(REFRESH_INTERVAL_MS).subscribe(() => {
      if (this.technicalPointId != null) {
        this.setupRetrySub?.unsubscribe();
        this.setupRetrySub = undefined;
        return;
      }
      this.resolveTechnicalPoint();
    });
  }

  private startPolling(): void {
    if (this.technicalPointId == null) {
      return;
    }

    // Guard against a retry starting a second timer alongside the first.
    this.refreshSub?.unsubscribe();

    const techPointId = this.technicalPointId;
    this.loadInspections(techPointId);
    // catchError lives INSIDE the switchMap on purpose: if the request error
    // reached the outer interval it would terminate the subscription and the
    // board would stop refreshing until the page was reloaded manually. Here a
    // failed poll yields null, the timer survives, and the next tick retries.
    this.refreshSub = interval(REFRESH_INTERVAL_MS)
      .pipe(switchMap(() => this.techInspService.getReservationScreenInspections(techPointId)
        .pipe(catchError(() => of(null)))))
      .subscribe(inspections => {
        // A failed poll keeps the last successful board on screen; the next
        // tick will pick up fresh data once the service is back.
        if (inspections != null) {
          this.applyInspections(inspections);
        }
      });
  }

  private loadInspections(techPointId: number): void {
    // The initial load must not flip loadFailed — that hides the whole board
    // behind the "invalid link" message. An empty board plus the polling
    // retry is the better failure mode for an unattended screen.
    this.techInspService.getReservationScreenInspections(techPointId)
      .subscribe({
        next: inspections => this.applyInspections(inspections ?? []),
        error: () => { }
      });
  }

  private applyInspections(inspections: TechnicalInspection[]): void {
    const started = inspections.filter(i => i.status?.code === STARTED_STATUS_CODE);
    const waiting = inspections
      .filter(i => i.status?.code !== STARTED_STATUS_CODE)
      .sort((a, b) => this.sortKey(a).localeCompare(this.sortKey(b)));

    this.inProgressCards = this.buildCards(started);
    this.nextInspections = waiting.slice(0, NEXT_LIMIT);
  }

  private buildCards(started: TechnicalInspection[]): BoardCard[] {
    // Render one card per valid line; fall back to one card per started
    // inspection when line metadata is unavailable.
    if (this.lineNumbers.length === 0) {
      return started.length > 0
        ? started.map(i => ({ lineNumber: this.lineNumberOf(i), inspection: i }))
        : [{ lineNumber: null, inspection: null }];
    }

    return this.lineNumbers.map(lineNumber => ({
      lineNumber,
      inspection: started.find(i => this.lineNumberOf(i) === lineNumber) ?? null
    }));
  }

  private lineNumberOf(inspection: TechnicalInspection): number | null {
    const time = inspection.techInspectionTimes?.find(t => t.isValid !== false);
    return time?.technicalPointWorkingHours?.line?.number ?? null;
  }

  public displayTime(inspection: TechnicalInspection): string {
    if (inspection.slot) {
      return inspection.slot;
    }
    const time = inspection.techInspectionTimes?.find(t => t.isValid !== false);
    return time?.technicalPointWorkingHours?.timeConstraint?.validFrom ?? '';
  }

  // The main service of an inspection, identified by its service type code
  // rather than by position — the services are not guaranteed to be ordered
  // with the main one first.
  public mainService(inspection: TechnicalInspection): string {
    const services = (inspection.techInspectionServices ?? [])
      .filter(s => s.isValid !== false);

    const main = services.find(s =>
      s.technicalPointService?.rvsService?.service?.serviceType?.code === MAIN_SERVICE_TYPE_CODE);

    return this.serviceName(main) ?? '';
  }

  private serviceName(service?: TechInspectionService): string | undefined {
    return service?.technicalPointService?.rvsService?.service?.name;
  }

  private sortKey(inspection: TechnicalInspection): string {
    return this.displayTime(inspection) || '99:99';
  }

  private updateClock(): void {
    const now = new Date();
    this.currentDate = this.pad(now.getDate()) + '.' + this.pad(now.getMonth() + 1) + '.' + now.getFullYear();
    this.currentTime = this.pad(now.getHours()) + ':' + this.pad(now.getMinutes());
  }

  private pad(value: number): string {
    return value < 10 ? '0' + value : '' + value;
  }
}
